import { nanoid } from '@reduxjs/toolkit';
import { defineMessages } from 'react-intl';

import { ValuesOf } from '@/shared/utils/types';
import type { TMake } from '@src-common/api/definitions/w4d-cars-types';

import { ENGINES, ENGINE_MESSAGES, type EngineType } from '../engine';

const COLORS = {
  BLACK: { title: 'Black', value: '#000000' },
  WHITE: { title: 'White / Cream', value: '#FFFFFF' }, // White, Pearl
  GRAY: { title: 'Gray', value: '#808080' },
  DARK_BLUE: { title: 'Dark Blue', value: '#00007F' },
  BROWN: { title: 'Brown', value: '#49311C' }, // Brown, Nappa
  SIENNA_BROWN: { title: 'Sienna Brown', value: '#875341' },
  SILVER: { title: 'Silver', value: '#C0C0C0' },
  BEIGE: { title: 'Beige', value: '#F5F5DC' },
  NAUTIC_BLUE: { title: 'Nautic Blue', value: '#1A5091' },
  BURGUNDY: { title: 'Burgundy', value: '#800020' },
  GREEN: { title: 'Green', value: '#354542' },
  NAPPA_LEATHER_BLACK: { title: 'Nappa Leather Black', value: '#0A0A0A' },
  NAPPA_LEATHER_SIENNA_BROWN_BLACK: {
    title: 'Nappa Leather Sienna Brown/Black',
    value: '#4A2C23',
  },
  BEIGE_FABRIC_ROOF_LINER: { title: 'Beige Fabric Roof Liner', value: '#E6D8C5' },
  NAPPA_LEATHER_PORCELAIN: { title: 'Nappa Leather Porcelain', value: '#F2F2F2' },
  NAPPA_LEATHER_BEIGE_SILK: { title: 'Nappa Leather Beige Silk', value: '#EAD8B0' },
  NAPPA_LEATHER_MACCHIATO_BEIGE_MAGMA_GREY: {
    title: 'Nappa Leather Macchiato Beige/Magma Grey',
    value: '#B0A69A',
  },
  BACKEN_BLACK: { title: 'Backen Black', value: '#1A1A1A' },
  SHEER_GRAY: { title: 'Sheer Gray', value: '#A0A0A0' },
  CAMELIA: { title: 'Camelia', value: '#E1A9A1' },
  BRANDY: { title: 'Brandy', value: '#8B5A2B' },
  WHISPER_BEIGE: { title: 'Whisper Beige', value: '#F1E7D6' },
  JET_BLACK: { title: 'Jet Black', value: '#343434' },
} as const;

type ColorValue = (typeof COLORS)[keyof typeof COLORS]['value'];

const DEFAULT_MAX_AGE = 3;
const DEFAULT_COLORS = [COLORS.BLACK, COLORS.WHITE];
const DEFAULT_INTERIOR_COLORS = [
  COLORS.BLACK,
  COLORS.WHITE,
  COLORS.BEIGE,
  COLORS.BROWN,
  COLORS.GRAY,
];

const londonColors = [COLORS.BLACK, COLORS.GRAY, COLORS.SILVER];
const londonInteriorColors = [COLORS.BLACK, COLORS.WHITE, COLORS.BEIGE, COLORS.BROWN, COLORS.GRAY];
const moscowColors = [COLORS.BLACK, COLORS.WHITE, COLORS.GRAY, COLORS.DARK_BLUE, COLORS.BROWN];

const MAKES = {
  MERCEDES: 'Mercedes-Benz',
  MERCEDES_MAYBACH: 'Mercedes-Maybach',
  BMW: 'BMW',
  LAND_ROVER: 'Land Rover',
  HONGQI: 'Hongqi',
  GMC: 'GMC',
  CADILLAC: 'Cadillac',
  CHEVROLET: 'Chevrolet',
} as const satisfies Record<string, TMake>;

const WHEELBASES = {
  COMPACT: 'compact',
  SHORT: 'short',
  REGULAR: 'regular',
  LONG: 'long',
  EXTRA_LONG: 'extra_long',
} as const;

const WHEELBASES_MESSAGES = defineMessages<Wheelbase>({
  compact: { defaultMessage: 'Compact', id: 'ydxyP+', description: 'Compact wheelbase option' },
  short: { defaultMessage: 'Short', id: 'EFrUzu', description: 'Short wheelbase option' },
  regular: {
    defaultMessage: 'Regular',
    id: 'global_wheelbase_regular',
    description: 'Regular wheelbase option',
  },
  long: { defaultMessage: 'Long (L)', id: 'eZzfb+', description: 'Long wheelbase option' },
  extra_long: {
    defaultMessage: 'Extra-Long (XL) ',
    id: 'jaNv5d',
    description: 'Extra-Long wheelbase option',
  },
});

const SERVICE_CLASSES = {
  BUSINESS: 'Business',
  FIRST: 'First',
  LUXE: 'Luxe',
  XL: 'XL',
  SUV: 'SUV',
  BUSINESS_SUV: 'Business SUV',
} as const;

type Wheelbase = ValuesOf<typeof WHEELBASES>;

const isWheelbase = (value: string): value is Wheelbase =>
  Object.values(WHEELBASES).includes(value as Wheelbase);

interface Modification {
  model: string;
  maxAge: number;
  generations: string[];
  color: ValuesOf<typeof COLORS>[];
  interiorColor: ValuesOf<typeof COLORS>[];
  wheelbases: ValuesOf<typeof WHEELBASES>[];
  serviceClass: ValuesOf<typeof SERVICE_CLASSES>;
  //Business = e_class | First = s_class | XL = v_class | Luxe = luxe | SUV = suv | Business SUV = business_suv
  tag: 'e_class' | 's_class' | 'v_class' | 'luxe' | 'suv' | 'business_suv';
  note?: boolean;
  minManufacturingYear?: number;
  engines: EngineType[];
}

interface Make {
  _id: string;
  make: TMake;
  modifications: Modification[];
}

const DEFAULT_ENGINES = [ENGINES.PETROL, ENGINES.DIESEL, ENGINES.HYBRID, ENGINES.ELECTRIC];

type Config = Record<string, Make[]>;

const MAYBACH: Make = {
  _id: nanoid(),
  make: MAKES.MERCEDES_MAYBACH,
  modifications: [
    {
      model: 'S-Class',
      color: moscowColors,
      interiorColor: DEFAULT_INTERIOR_COLORS,
      maxAge: 5,
      generations: ['X222', 'X223', 'Z223'],
      wheelbases: [],
      serviceClass: SERVICE_CLASSES.LUXE,
      tag: 'luxe',
      engines: DEFAULT_ENGINES,
    },
  ],
};

const cityToModels: Config = {
  moscow: [
    {
      _id: nanoid(),
      make: MAKES.MERCEDES,
      modifications: [
        {
          model: 'E-Class',
          maxAge: 4,
          generations: ['W213', 'W214'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.SHORT],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'S-Class',
          maxAge: 5,
          generations: ['W222', 'W223'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.SHORT, WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.FIRST,
          tag: 's_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'V-Class',
          maxAge: 5,
          generations: ['W447'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.COMPACT, WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQE',
          maxAge: DEFAULT_MAX_AGE,
          generations: ['V295'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQS',
          maxAge: 5,
          generations: ['V297'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQV',
          maxAge: 5,
          generations: ['W447'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    MAYBACH,
    {
      _id: nanoid(),
      make: MAKES.BMW,
      modifications: [
        {
          model: '5 Series',
          maxAge: 4,
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          generations: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: '7 Series',
          maxAge: 5,
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          generations: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
  ],
  spb: [
    {
      _id: nanoid(),
      make: MAKES.MERCEDES,
      modifications: [
        {
          model: 'E-Class',
          maxAge: 4,
          generations: ['W213', 'W214'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.SHORT],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'S-Class',
          maxAge: 5,
          generations: ['W222', 'W223'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.SHORT, WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.FIRST,
          tag: 's_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'V-Class',
          maxAge: 5,
          generations: ['W447'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.COMPACT, WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQE',
          generations: ['V295'],
          maxAge: DEFAULT_MAX_AGE,
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQS',
          maxAge: 5,
          generations: ['V297'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQV',
          maxAge: 5,
          generations: ['W447'],
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    MAYBACH,
    {
      _id: nanoid(),
      make: MAKES.BMW,
      modifications: [
        {
          model: '5 Series',
          maxAge: 2,
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          generations: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: '7 Series',
          maxAge: 2,
          color: moscowColors,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          generations: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
  ],
  london: [
    {
      _id: nanoid(),
      make: MAKES.MERCEDES,
      modifications: [
        {
          model: 'E-Class',
          maxAge: 3,
          color: [...londonColors, COLORS.NAUTIC_BLUE],
          interiorColor: londonInteriorColors,
          generations: ['W213', 'W214'],
          wheelbases: [WHEELBASES.SHORT],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'S-Class',
          maxAge: 3,
          color: londonColors,
          interiorColor: londonInteriorColors,
          generations: ['W222', 'W223'],
          wheelbases: [WHEELBASES.SHORT, WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.FIRST,
          tag: 's_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'V-Class',
          maxAge: 4,
          color: londonColors,
          interiorColor: londonInteriorColors,
          generations: ['W447'],
          wheelbases: [WHEELBASES.COMPACT, WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQE',
          maxAge: 3,
          color: londonColors,
          interiorColor: londonInteriorColors,
          generations: ['V295'],
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
          note: true,
        },
        {
          model: 'EQS',
          maxAge: 3,
          color: londonColors,
          interiorColor: londonInteriorColors,
          generations: ['V297'],
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQV',
          maxAge: 4,
          color: londonColors,
          interiorColor: londonInteriorColors,
          generations: ['W447'],
          wheelbases: [WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.BMW,
      modifications: [
        {
          model: '5 Series',
          maxAge: 3,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BROWN, COLORS.BURGUNDY],
          wheelbases: [WHEELBASES.SHORT],
          generations: ['G60'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'i5',
          maxAge: 3,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BROWN, COLORS.BURGUNDY],
          wheelbases: [WHEELBASES.SHORT],
          generations: ['G60'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: '7 Series',
          maxAge: 3,
          minManufacturingYear: 2022,
          color: [COLORS.BLACK, COLORS.SILVER, COLORS.GRAY, COLORS.DARK_BLUE],
          interiorColor: londonInteriorColors,
          wheelbases: [WHEELBASES.LONG],
          generations: ['G70'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'i7',
          maxAge: 3,
          minManufacturingYear: 2022,
          color: [COLORS.BLACK, COLORS.SILVER, COLORS.GRAY, COLORS.DARK_BLUE],
          interiorColor: londonInteriorColors,
          wheelbases: [WHEELBASES.LONG],
          generations: ['G70'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.LAND_ROVER,
      modifications: [
        {
          model: 'Range Rover',
          maxAge: Math.min(new Date().getFullYear() - 2023, 4),
          minManufacturingYear: 2023,
          color: [COLORS.BLACK, COLORS.GRAY, COLORS.SILVER, COLORS.NAUTIC_BLUE, COLORS.GREEN],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BROWN, COLORS.BURGUNDY],
          wheelbases: [WHEELBASES.LONG],
          generations: ['L460'],
          serviceClass: SERVICE_CLASSES.SUV,
          tag: 'suv',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
  ],
  paris: [
    {
      _id: nanoid(),
      make: MAKES.MERCEDES,
      modifications: [
        {
          model: 'E-Class',
          maxAge: 4,
          color: [COLORS.BLACK, COLORS.GRAY, COLORS.NAUTIC_BLUE],
          interiorColor: londonInteriorColors,
          generations: ['W213', 'W214'],
          wheelbases: [WHEELBASES.SHORT],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'S-Class',
          maxAge: 5,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: londonInteriorColors,
          generations: ['W222', 'W223'],
          wheelbases: [WHEELBASES.SHORT, WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.FIRST,
          tag: 's_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'V-Class',
          maxAge: 3,
          color: [COLORS.BLACK],
          interiorColor: londonInteriorColors,
          generations: ['W447'],
          wheelbases: [WHEELBASES.COMPACT, WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQE',
          maxAge: 4,
          color: [COLORS.BLACK],
          interiorColor: londonInteriorColors,
          generations: ['V295'],
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQS',
          maxAge: 4,
          color: [COLORS.BLACK],
          interiorColor: londonInteriorColors,
          generations: ['V297'],
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQV',
          maxAge: 3,
          color: [COLORS.BLACK],
          interiorColor: londonInteriorColors,
          generations: ['W447'],
          wheelbases: [WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.MERCEDES_MAYBACH,
      modifications: [
        {
          model: 'S-Class',
          maxAge: 5,
          minManufacturingYear: 2023,
          color: [COLORS.BLACK, COLORS.GRAY, COLORS.SILVER],
          interiorColor: [COLORS.BLACK, COLORS.BROWN, COLORS.WHITE],
          wheelbases: [WHEELBASES.LONG],
          generations: ['X223', 'Z223'],
          serviceClass: SERVICE_CLASSES.LUXE,
          tag: 'luxe',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.BMW,
      modifications: [
        {
          model: '5 Series',
          maxAge: 4,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BROWN, COLORS.BURGUNDY],
          wheelbases: [WHEELBASES.SHORT],
          generations: ['G60'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'i5',
          maxAge: 4,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BROWN, COLORS.BURGUNDY],
          wheelbases: [WHEELBASES.SHORT],
          generations: ['G60'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
  ],
  dubai: [
    {
      _id: nanoid(),
      make: MAKES.MERCEDES,
      modifications: [
        {
          model: 'E-Class',
          maxAge: 3,
          color: [COLORS.BLACK, COLORS.GRAY, COLORS.SILVER],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BEIGE, COLORS.BROWN, COLORS.GRAY],
          generations: ['W214'],
          wheelbases: [WHEELBASES.SHORT],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'S-Class',
          generations: ['W223'],
          maxAge: DEFAULT_MAX_AGE,
          color: [COLORS.BLACK],
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.SHORT, WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.FIRST,
          tag: 's_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'V-Class',
          generations: ['W447'],
          maxAge: DEFAULT_MAX_AGE,
          color: [COLORS.BLACK],
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.COMPACT, WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQE',
          maxAge: 3,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BEIGE, COLORS.BROWN, COLORS.GRAY],
          generations: ['V295'],
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQS',
          maxAge: 3,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [COLORS.BLACK, COLORS.WHITE, COLORS.BEIGE, COLORS.BROWN, COLORS.GRAY],
          generations: ['V297'],
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQV',
          generations: ['W447'],
          maxAge: DEFAULT_MAX_AGE,
          color: [COLORS.BLACK],
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.MERCEDES_MAYBACH,
      modifications: [
        {
          model: 'S-Class',
          generations: ['X222', 'X223', 'Z223', 'W223'],
          minManufacturingYear: 2024,
          maxAge: 3,
          color: [COLORS.BLACK],
          interiorColor: [COLORS.WHITE, COLORS.BEIGE, COLORS.SIENNA_BROWN],
          wheelbases: [WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.LUXE,
          tag: 'luxe',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.BMW,
      modifications: [
        {
          model: '5 Series',
          maxAge: 1,
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [
            COLORS.BLACK,
            COLORS.WHITE,
            COLORS.BEIGE,
            COLORS.BROWN,
            COLORS.BURGUNDY,
            COLORS.GRAY,
          ],
          wheelbases: [],
          generations: ['G60'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: '7 Series',
          maxAge: 3,
          minManufacturingYear: 2024,
          color: [COLORS.BLACK],
          interiorColor: [COLORS.BURGUNDY, COLORS.BROWN],
          wheelbases: [WHEELBASES.LONG],
          generations: ['G70'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: [ENGINES.PETROL, ENGINES.ELECTRIC],
        },
        {
          model: 'i7',
          maxAge: 3,
          minManufacturingYear: 2024,
          color: [COLORS.BLACK],
          interiorColor: [COLORS.BURGUNDY, COLORS.BROWN],
          wheelbases: [WHEELBASES.LONG],
          generations: ['G70'],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: [ENGINES.PETROL, ENGINES.ELECTRIC],
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.GMC,
      modifications: [
        {
          model: 'Yukon',
          maxAge: 5,
          minManufacturingYear: 2023,
          color: [COLORS.BLACK],
          interiorColor: [COLORS.BEIGE, COLORS.BROWN, COLORS.BLACK],
          generations: ['5th Gen'],
          wheelbases: [WHEELBASES.SHORT, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.SUV,
          tag: 'suv',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
  ],
  sochi: [
    {
      _id: nanoid(),
      make: MAKES.MERCEDES,
      modifications: [
        {
          model: 'E-Class',
          maxAge: 4,
          color: DEFAULT_COLORS,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          generations: ['W213', 'W214'],
          wheelbases: [WHEELBASES.SHORT],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'S-Class',
          generations: ['W223'],
          maxAge: DEFAULT_MAX_AGE,
          color: DEFAULT_COLORS,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [WHEELBASES.SHORT, WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.FIRST,
          tag: 's_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'V-Class',
          maxAge: DEFAULT_MAX_AGE,
          color: [COLORS.BLACK],
          interiorColor: DEFAULT_INTERIOR_COLORS,
          generations: ['W447'],
          wheelbases: [WHEELBASES.COMPACT, WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQE',
          maxAge: DEFAULT_MAX_AGE,
          generations: ['V295'],
          interiorColor: DEFAULT_INTERIOR_COLORS,
          color: DEFAULT_COLORS,
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQS',
          maxAge: DEFAULT_MAX_AGE,
          generations: ['V297'],
          color: DEFAULT_COLORS,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'EQV',
          maxAge: DEFAULT_MAX_AGE,
          color: [COLORS.BLACK],
          interiorColor: DEFAULT_INTERIOR_COLORS,
          generations: ['W447'],
          wheelbases: [WHEELBASES.LONG, WHEELBASES.EXTRA_LONG],
          serviceClass: SERVICE_CLASSES.XL,
          tag: 'v_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.BMW,
      modifications: [
        {
          model: '5 Series',
          maxAge: 2,
          color: DEFAULT_COLORS,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          generations: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
        {
          model: '7 Series',
          maxAge: 2,
          color: DEFAULT_COLORS,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          generations: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.HONGQI,
      modifications: [
        {
          model: 'H9',
          maxAge: 1,
          color: DEFAULT_COLORS,
          interiorColor: DEFAULT_INTERIOR_COLORS,
          wheelbases: [],
          generations: [],
          serviceClass: SERVICE_CLASSES.BUSINESS,
          tag: 'e_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
  ],
  nyc: [
    {
      _id: nanoid(),
      make: MAKES.MERCEDES,
      modifications: [
        {
          model: 'S-Class',
          maxAge: DEFAULT_MAX_AGE,
          minManufacturingYear: 2023,
          generations: ['W223'],
          color: [COLORS.BLACK, COLORS.SILVER, COLORS.GRAY],
          interiorColor: [
            COLORS.NAPPA_LEATHER_BLACK,
            COLORS.NAPPA_LEATHER_SIENNA_BROWN_BLACK,
            COLORS.BEIGE_FABRIC_ROOF_LINER,
            COLORS.NAPPA_LEATHER_PORCELAIN,
            COLORS.NAPPA_LEATHER_BEIGE_SILK,
            COLORS.NAPPA_LEATHER_MACCHIATO_BEIGE_MAGMA_GREY,
          ],
          wheelbases: [WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.FIRST,
          tag: 's_class',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
    {
      _id: nanoid(),
      make: MAKES.CADILLAC,
      modifications: [
        {
          model: 'Escalade ESV',
          maxAge: DEFAULT_MAX_AGE,
          minManufacturingYear: 2023,
          generations: ['GMT1UL'],
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [
            COLORS.BACKEN_BLACK,
            COLORS.SHEER_GRAY,
            COLORS.CAMELIA,
            COLORS.BRANDY,
            COLORS.JET_BLACK,
            COLORS.WHISPER_BEIGE,
          ],
          wheelbases: [WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.BUSINESS_SUV,
          tag: 'business_suv',
          engines: DEFAULT_ENGINES,
        },
        {
          model: 'Escalade IQL',
          maxAge: DEFAULT_MAX_AGE,
          minManufacturingYear: 2023,
          generations: ['GMT1UL'],
          color: [COLORS.BLACK, COLORS.GRAY],
          interiorColor: [
            COLORS.BACKEN_BLACK,
            COLORS.SHEER_GRAY,
            COLORS.CAMELIA,
            COLORS.BRANDY,
            COLORS.JET_BLACK,
            COLORS.WHISPER_BEIGE,
          ],
          wheelbases: [WHEELBASES.LONG],
          serviceClass: SERVICE_CLASSES.BUSINESS_SUV,
          tag: 'business_suv',
          engines: DEFAULT_ENGINES,
        },
      ],
    },
  ],
};

const getHardcodedCarModels = (city: string) => {
  switch (city) {
    case 'moscow':
      return cityToModels.moscow;
    case 'spb':
      return cityToModels.spb;
    case 'london':
      return cityToModels.london;
    case 'paris':
      return cityToModels.paris;
    case 'dubai':
      return cityToModels.dubai;
    case 'sochi':
      return cityToModels.sochi;
    case 'nyc':
      return cityToModels.nyc;
    default:
      return cityToModels.london;
  }
};

export type { Wheelbase, Modification, Make, ColorValue, EngineType };
export {
  getHardcodedCarModels,
  WHEELBASES_MESSAGES,
  isWheelbase,
  DEFAULT_COLORS,
  DEFAULT_MAX_AGE,
  DEFAULT_INTERIOR_COLORS,
  MAKES,
  ENGINES,
  ENGINE_MESSAGES,
};
